import { ContactLink, LegalPage, type LegalSection } from './LegalPage';

export const PRIVACY_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Who runs PainPal',
    blocks: [
      'PainPal is run by Donovan Crewe, who decides how your data is used and is responsible for it (the “controller” under the EU and UK GDPR).',
      <>Questions, requests and complaints: <ContactLink />.</>,
    ],
  },
  {
    title: 'What we collect',
    blocks: [
      [
        'Your account: your name (as you typed it, or as your Strava profile gave it if you signed up with Strava), email address, a hash of your password (never the password itself), your timezone, and a session cookie that keeps you signed in.',
        'Your Strava connection, if you connect Strava: your Strava athlete ID, name and profile picture link, the access tokens Strava issues to PainPal (stored encrypted), the permissions you granted, and the FTP on your Strava profile.',
        'Your activities, from Strava or from .FIT files you upload: name, sport, date and time, duration, distance, elevation, and recordings such as power, heart rate, speed, cadence, altitude and GPS position. We also keep the .FIT files you upload.',
        'What PainPal works out from them: Load, Intensity, Fitness, Fatigue, Form, time in zones and power curves.',
        'Your settings: thresholds (FTP, threshold pace, threshold and maximum heart rate), heart-rate zone choices, your training plan and your timezone.',
        'Your agreement: when you agreed to PainPal processing your heart rate and activity data.',
      ],
      'Heart rate and similar recordings can say something about your health, so PainPal asks for your explicit consent to process them (see below).',
    ],
  },
  {
    title: 'Why we use it, and on what legal basis',
    blocks: [
      [
        'To provide PainPal to you: storing your activities and working out your numbers. This is needed for our agreement with you (contract).',
        'To connect Strava and import your activities: your consent, which you can withdraw at any time by disconnecting Strava in Settings or revoking PainPal’s access in your Strava settings.',
        'Heart rate and other health-related recordings: your explicit consent. You give it by ticking the box “I’m 16 or older, and I agree PainPal may process my heart rate and activity data to work out my training numbers.” before you create an account or sign in with Strava. An account created before that box existed is asked once, in the app. PainPal records when you agreed. You withdraw your consent by deleting your account.',
        'To keep PainPal secure and working, for example server logs that record your account ID and what went wrong (never passwords or tokens): our legitimate interest in running a reliable service.',
      ],
    ],
  },
  {
    title: 'Who else handles it',
    blocks: [
      'These providers process data for PainPal under its instructions:',
      [
        'Amazon Web Services: hosting, file storage, job queues and server logs, in the United States (us-east-1, North Virginia).',
        'Neon: the database that holds your account, activities and settings.',
        'Strava: the source of your Strava activities when you connect Strava. Strava’s own privacy policy covers the data Strava holds.',
      ],
      'Your browser also loads fonts from Google Fonts and route map tiles from OpenStreetMap’s servers, which see your IP address and the map area shown.',
      'We do not share your data with anyone else, unless the law requires it.',
    ],
  },
  {
    title: 'International transfers',
    blocks: [
      'PainPal is hosted by Amazon Web Services in the United States, so your data leaves the UK and the EU. Where your data leaves the UK or the EU, we rely on the safeguards the law provides, such as the EU-US Data Privacy Framework or the European Commission’s standard contractual clauses with our providers.',
    ],
  },
  {
    title: 'How long we keep it',
    blocks: [
      'We keep your data until you delete your account.',
      'Strava data is deleted when Strava access ends: when you disconnect Strava in Settings, when you revoke PainPal’s access on Strava, or when Strava stops accepting PainPal’s access. That deletes every activity imported from Strava, its recordings and files, the stored Strava tokens, and the Strava name, profile picture link and FTP PainPal kept with the connection. Uploaded files, thresholds and your plan stay; a threshold that named a Strava activity as its source keeps its value but loses the name where PainPal can still tell which ride it came from.',
      'Your account’s name stays, even if it came from your Strava profile when you signed up: it is your account’s name now, and you can ask us to change it. Your account also keeps your Strava athlete ID, because signing in with Strava again finds your account by it. If you signed up with Strava and have not set an email, your account’s email is a placeholder made from that ID (strava-ID@users.painpal.invalid), and it stays too. Disconnecting Strava in Settings, which needs an email and password first, removes the athlete ID; deleting your account removes everything.',
      'Deleted data can survive in database backups until they expire, within our database provider’s backup retention window.',
    ],
  },
  {
    title: 'Your rights',
    blocks: [
      [
        'Access: ask for a copy of the data we hold about you.',
        'Rectification: correct your details and thresholds in Settings, or ask us to.',
        'Erasure: delete your account and all its data with Delete account in Settings.',
        'Portability and objection: email us and we will send your data in a common format, or stop processing you object to.',
        'Withdrawing consent: delete your account to withdraw your consent to processing your heart rate and activity data, or disconnect Strava to withdraw your consent to the Strava connection, at any time.',
        'Complaint: you can complain to a data protection supervisory authority, such as the ICO in the UK or the authority where you live in the EU.',
      ],
      <>To use any of these rights, email <ContactLink />.</>,
    ],
  },
  {
    title: 'What we never do',
    blocks: [
      [
        'We never sell your data.',
        'We show no ads.',
        'We use no tracking or analytics cookies. We use only strictly necessary cookies and browser storage: to keep you signed in, to complete Strava sign-in and connect, and to carry your agreement through sign-up and Strava sign-in.',
        'We never use your Strava data or activity data to train AI or machine learning models.',
      ],
    ],
  },
  {
    title: 'Age',
    blocks: ['You must be 16 or older to use PainPal. You confirm it when you agree to the processing above.'],
  },
  {
    title: 'Changes to this policy',
    blocks: ['We post any change on this page with a new effective date.'],
  },
];

export function PrivacyPage() {
  return <LegalPage title="PRIVACY" sections={PRIVACY_SECTIONS} />;
}
